/**
 * Browser token storage for the dashboard's session JWT.
 *
 * The platform moved from HttpOnly cookies (`cc_session` + `cc_csrf`) to
 * `Authorization: Bearer <jwt>` for the dashboard SPA. The JWT lives in
 * `localStorage` under {@link TOKEN_STORAGE_KEY} and is read on every
 * request by the {@link AuthClient}.
 *
 * Trade-offs accepted with this design (vs cookies):
 *   - XSS that runs in the dashboard origin can read the token. Mitigation:
 *     strict CSP + sanitize all rendered HTML in the dashboard app.
 *   - No automatic clearing on browser close — the token sits until `exp`
 *     (8h) or an explicit `clear()` call.
 *
 * SSR / Node-test safety: every accessor guards `typeof localStorage`
 * (and the older `typeof window`) so the module can be imported in
 * Vitest / Node test runners without crashing.
 */
export const TOKEN_STORAGE_KEY = "cc_token";
function storage() {
    if (typeof localStorage === "undefined")
        return null;
    return localStorage;
}
/**
 * Read the current session JWT. Returns `null` when the user is
 * unauthenticated, when `localStorage` is unavailable (SSR / Node), or
 * when the underlying read throws (e.g., disabled storage in the browser).
 */
export function getToken() {
    const s = storage();
    if (!s)
        return null;
    try {
        const v = s.getItem(TOKEN_STORAGE_KEY);
        return v && v.length > 0 ? v : null;
    }
    catch {
        return null;
    }
}
/**
 * Persist a freshly minted session JWT. No-op when storage is unavailable.
 * Callers should immediately use the token they just stored — do not rely
 * on a subsequent `getToken()` in the same tick when storage is broken.
 */
export function setToken(token) {
    const s = storage();
    if (!s)
        return;
    try {
        s.setItem(TOKEN_STORAGE_KEY, token);
    }
    catch {
        // Quota / privacy-mode failures are silent on purpose: the caller's
        // happy path (response had a token) shouldn't blow up the request.
    }
}
/** Remove the stored token. Used on logout and 401 responses. */
export function clearToken() {
    const s = storage();
    if (!s)
        return;
    try {
        s.removeItem(TOKEN_STORAGE_KEY);
    }
    catch {
        // Same rationale as setToken: failures here are recoverable.
    }
}
/**
 * Convenience namespace for callers that prefer `tokenStore.get()` style.
 * Equivalent to importing the individual functions.
 */
export const tokenStore = {
    get: getToken,
    set: setToken,
    clear: clearToken,
};
//# sourceMappingURL=token-store.js.map